import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onGetStarted, onLearnMore }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const carImages = [
    {
      alt: "Silver SUV sold successfully",
      gradient: "from-slate-400 to-slate-600"
    },
    {
      alt: "Red sedan sold successfully", 
      gradient: "from-red-400 to-red-600"
    },
    {
      alt: "Blue hatchback sold successfully",
      gradient: "from-blue-400 to-blue-600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [carImages.length]);

  return (
    <section className="relative pt-8 pb-16 lg:pt-16 lg:pb-24 bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 lg:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className="text-center lg:text-left space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
              <Icon name="Shield" size={16} />
              <span>Trusted by 50,000+ sellers</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
                Sell Your Car in{' '}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Minutes
                </span>
                <br />
                Not Weeks
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground max-w-xl">
                Get instant, guaranteed offers from trusted dealers. No haggling, no hassle, no hidden fees.
                Get paid the same day.
              </p>
            </div>

            {/* Value Props */}
            <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-green-600" />
                <span>Offers in 24 hours</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Truck" size={16} className="text-blue-600" />
                <span>Free pickup anywhere</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="DollarSign" size={16} className="text-purple-600" />
                <span>Instant payment</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="xl" 
                onClick={onGetStarted}
                iconName="ArrowRight" 
                iconPosition="right"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Get My Offer Now
              </Button>
              <Button 
                size="xl" 
                variant="outline" 
                onClick={onLearnMore}
                iconName="Play"
                iconPosition="left"
              >
                See How It Works
              </Button>
            </div>

            {/* Social Proof */}
            <div className="pt-8 border-t border-border">
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-8">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full border-2 border-white"></div>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">2,847</span> cars sold this month
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
                  ))}
                  <span className="text-sm text-muted-foreground ml-2">
                    <span className="font-semibold text-foreground">4.9</span> average rating
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Car Showcase */}
          <div className="relative">
            {/* Main Car Display */}
            <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-2xl">
              {carImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className={`w-full h-full bg-gradient-to-br ${image.gradient} flex items-center justify-center`}>
                    <Icon name="Car" size={120} color="white" className="opacity-80" />
                  </div>
                  <div className="absolute inset-0 bg-black/10"></div>
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600 font-semibold">SOLD</span>
                      <span className="text-sm text-muted-foreground">in 2 hours</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Sale Badge */}
              <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                $24,500 SOLD
              </div>
            </div>

            {/* Image Indicators */}
            <div className="flex justify-center space-x-2 mt-6">
              {carImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentImageIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Floating Success Cards */}
            <div className="absolute -top-4 -left-4 bg-white rounded-lg shadow-lg p-3 border border-border hidden lg:block">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Live offer: $18,900</span>
              </div>
            </div>
            
            <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-lg p-3 border border-border hidden lg:block">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span className="text-sm font-medium">Payment received</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;